import type { SupabaseClient } from "@supabase/supabase-js";
import { supabase } from "../config/supabaseClient";

type UserLinkRow = {
  collection_name: string | null;
  parent_id: string | number | null;
};

/** True when the live landing stack has at least one carousel/link section with content. */
export function hasVisibleLandingContent(
  components?: {
    data?: any[];
    showingComponents?: any[];
  } | null,
  options?: {
    hasExpandedSection?: boolean;
    shouldRenderInlineExpandedSection?: boolean;
  },
): boolean {
  if (options?.hasExpandedSection || options?.shouldRenderInlineExpandedSection) {
    return true;
  }

  const visible = components?.showingComponents ?? [];
  if (!visible.length) {
    return false;
  }

  return visible.some((item) => item?.isEmpty !== true);
}

/**
 * Links sections without saved links should not count as landing content.
 * Mutates component rows in memory so `isEmpty` reflects link rows.
 * Pass `preloadedLinks` (including `[]`) to skip the `user_links` round-trip.
 */
export async function enrichLinksSectionEmptyState(
  components: any[],
  user_id: string,
  from: string | undefined,
  client: SupabaseClient = supabase,
  preloadedLinks?: UserLinkRow[] | null,
): Promise<void> {
  const linksSections = components.filter((c) => c?.type === "links-section");
  if (!linksSections.length || !user_id) return;

  let links: UserLinkRow[];
  if (preloadedLinks !== undefined) {
    links = preloadedLinks ?? [];
  } else {
    const linkType = from === "embed-search" ? "embed-search" : "landing-page";
    const { data } = await client
      .from("user_links")
      .select("collection_name, parent_id")
      .eq("user_id", user_id)
      .eq("type", linkType)
      .eq("status", "active");
    links = data ?? [];
  }

  if (!links.length) {
    for (const component of linksSections) {
      component.isEmpty = true;
    }
    return;
  }

  const parentIds = new Set(
    links.map((link:any) => String(link.parent_id || "")).filter(Boolean),
  );
  const collectionNames = new Set(
    links
      .map((link:any) => String(link.collection_name || "").trim())
      .filter(Boolean),
  );

  for (const component of linksSections) {
    const hasLinks =
      (component.id != null && parentIds.has(String(component.id))) ||
      (component.name &&
        collectionNames.has(String(component.name).trim()));
    component.isEmpty = !hasLinks;
  }
}

/** Whether the search UI should start open (URL search params only). */
export function shouldDefaultToSearchFeed(args: {
  checkedType?: string | null;
  hasVisibleLanding: boolean;
  hasSearchParams: boolean;
  isDetailView?: boolean;
}): boolean {
  return args.hasSearchParams;
}
